import React, { useState, useEffect } from 'react';
import axios from 'axios';
import NewLeadModal from './NewLeadModal';
import LeadContextMenu from './LeadContextMenu';
import OpenLeadByIdModal from './OpenLeadByIdModal';
import { openLeadInNewTab } from './openLeadInNewTab';
import EditLeadModal from './EditLeadModal';
import EmailModal from './EmailModal';
import LeadDetailsModal from './LeadDetailsModal';
import BulkActionModal from './BulkActionModal';
import CsvGuideModal from './CsvGuideModal';
import LeadsImportModal from './LeadsImportModal';
import SaveSegmentModal from './SaveSegmentModal';
import SegmentsListModal from './SegmentsListModal';
import LeadsTableWithShared from './LeadsTableWithShared';
import { hasPermission, getLeadsViewAccess, canPerformSegmentAction, getCrossTabStorageKeys } from '../../utils/permissions';
import { DeleteConfirmationModal, ConfirmationModal } from '../common';
import Button from '../common/Button';
import { addUndoNotification } from '../common/NotificationManager';

const LeadsListWithShared = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [dealCounts, setDealCounts] = useState({});
  const [openLeadByIdModalOpen, setOpenLeadByIdModalOpen] = useState(false);
  // Initialize limit from localStorage or default to 20
  const initialLimit = parseInt(localStorage.getItem('leadsTableLimit') || '20', 10);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: [20, 50, 100].includes(initialLimit) ? initialLimit : 20, // Ensure valid limit
    total: 0,
    pages: 0
  });
  const [users, setUsers] = useState([]);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [csvImportStatus, setCsvImportStatus] = useState({
    loading: false,
    success: null,
    message: '',
    totalRows: 0,
    importedCount: 0,
    errors: []
  });
  // State for error modal
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');
  const [showCsvGuide, setShowCsvGuide] = useState(false);
  const fileInputRef = React.useRef(null);
  const [showFilters, setShowFilters] = useState(() => {
    return localStorage.getItem('leadsShowFilters') === 'true';
  });
  const [filters, setFilters] = useState(() => {
    const savedFilters = localStorage.getItem('leadsFilters');
    return savedFilters ? JSON.parse(savedFilters) : {
      assigned_to: '',
      status: '',
      country: '',
      original_source: '',
      latest_source: '',
      call_attempts_min: '',
      call_attempts_max: '',
      created_after: '',
      created_before: '',
      search: '',
      reconversion_date_after: '',
      reconversion_date_before: '',
      last_conversion_date_after: '',
      last_conversion_date_before: '',
      subscribed: '',
      courses_bought: '',
      has_courses: '',
      no_courses: ''
    };
  });
  const [activeFilters, setActiveFilters] = useState(() => {
    const savedActiveFilters = localStorage.getItem('leadsActiveFilters');
    return savedActiveFilters ? JSON.parse(savedActiveFilters) : {};
  });
  // Initialize sort preferences from localStorage or use defaults
  const [sortBy, setSortBy] = useState(() => {
    return localStorage.getItem('leadsTableSortBy') || 'created_at';
  });
  const [sortOrder, setSortOrder] = useState(() => {
    return localStorage.getItem('leadsTableSortOrder') || 'desc';
  });
  // Bulk selection state
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showBulkActionModal, setShowBulkActionModal] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [bulkActionData, setBulkActionData] = useState({
    status: '',
    assigned_to: '',
    country: '',
    call_attempts: ''
  });
  // Column management state
  const [showColumnModal, setShowColumnModal] = useState(false);
  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Segment state
  const [showSaveSegmentModal, setShowSaveSegmentModal] = useState(false);
  const [showSegmentsListModal, setShowSegmentsListModal] = useState(false);
  const [activeSegment, setActiveSegment] = useState(null);
  const [manualSegments, setManualSegments] = useState([]); // State for manual segments list

  // Check if user has permissions to perform different actions
  const canCreate = hasPermission('leads', 'create');
  const canUpdate = hasPermission('leads', 'update');
  const canDelete = hasPermission('leads', 'delete');
  const canImport = hasPermission('leads', 'import');
  const canExport = hasPermission('leads', 'export');
  const canCreateOwnSegments = canPerformSegmentAction('create_own');
  const leadsViewAccess = getLeadsViewAccess();

  // Fetch leads data
  // Modified to accept an optional limit override
  const fetchLeads = async (page = pagination.page, limitOverride = null) => {
    const currentLimit = limitOverride !== null ? limitOverride : pagination.limit;
    try {
      setLoading(true);

      const token = localStorage.getItem('token');
      if (!token) return;

      // Build query parameters from active filters
      const params = {
        ...activeFilters,
        page: page,
        limit: currentLimit, // Use currentLimit here
        sort_by: sortBy,
        sort_order: sortOrder
      };

      // Only add segmentId if activeSegment is set and not null
      if (activeSegment && activeSegment.id) {
        params.segmentId = activeSegment.id;
      } else {
        // Make sure that any courses_bought, has_courses, and no_courses parameters
        // are explicitly set to empty if they exist in activeFilters (to override any cached values)
        if (!params.courses_bought) params.courses_bought = '';
        if (!params.has_courses) params.has_courses = '';
        if (!params.no_courses) params.no_courses = '';
      }

      // Add debug log to see what params are being sent
      console.log('API request params:', params);

      const config = {
        headers: {
          'x-auth-token': token
        },
        params: params
      };

      console.log('Sending API request with config:', config);
      const response = await axios.get('/api/leads', config);

      console.log('API response:', response.data);

      const leadsList = response.data.leads || [];

      setLeads(leadsList);
      // The API returns pagination data in a "pagination" object, not at the root level
      const paginationData = {
        page: parseInt(response.data.pagination?.page || 1),
        limit: parseInt(response.data.pagination?.limit || 20),
        total: parseInt(response.data.pagination?.total || 0),
        pages: parseInt(response.data.pagination?.pages || 1)
      };
      console.log('Setting pagination data:', paginationData);
      setPagination(paginationData);
      setError('');
    } catch (err) {
      console.error('Error fetching leads:', err);

      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch leads';
      setError(`Error: ${errorMessage}. Please try again or contact support.`);
    } finally {
      setLoading(false);
    }
  };

  // Fetch deal counts for all leads
  const fetchDealCounts = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const config = {
        headers: {
          'x-auth-token': token
        }
      };

      const response = await axios.get('/api/leads/counts/deals', config);

      // The response structure from this endpoint is different:
      // It returns an object where keys are lead_ids and values are counts
      setDealCounts(response.data);
    } catch (err) {
      console.error('Error fetching deal counts:', err);
    }
  };

  // Fetch users for assignment
  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const config = {
        headers: {
          'x-auth-token': token
        }
      };

      const response = await axios.get('/api/auth/users?has_lead_access=true', config);
      setUsers(response.data);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  // Initial fetch when component mounts
  // Set up cross-tab communication listener
  useEffect(() => {
    const storageKeys = getCrossTabStorageKeys();

    const handleStorageChange = (e) => {
      // Check if the change is a lead update
      if (e.key === storageKeys.LEAD_UPDATED ||
          e.key === storageKeys.LEAD_CREATED ||
          e.key === storageKeys.LEAD_DELETED) {

        console.log('Cross-tab lead change detected:', e.key, e.newValue);

        // Refresh leads data
        fetchLeads(pagination.page); // Refresh current page

        // Refresh deal counts too if a lead was updated
        if (e.key === storageKeys.LEAD_UPDATED) {
          fetchDealCounts();
        }
      }
    };

    // Add event listener
    window.addEventListener('storage', handleStorageChange);

    // Cleanup listener on component unmount
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [pagination.page]); // Re-attach listener if page changes (though maybe not necessary)

  useEffect(() => {
    console.log('Fetching leads with filters:', activeFilters);
    console.log('Current sort state:', { sortBy, sortOrder });
    fetchLeads(); // Fetch leads based on current state (page, limit, filters, sort)
    fetchDealCounts();
    fetchUsers();
  }, [sortBy, sortOrder, activeFilters, activeSegment?.id]); // Depend on activeSegment.id instead of the object


  // Handle page change for pagination
  const handlePageChange = (newPage) => {
    console.log(`Changing to page ${newPage}`);
    fetchLeads(newPage);
  };

  // Handle limit change for pagination
  const handleLimitChange = (newLimit) => {
    const limitValue = parseInt(newLimit, 10);
    if (![20, 50, 100].includes(limitValue)) return; // Ignore invalid values

    console.log(`Changing limit to ${limitValue}`);
    // Save the new limit to localStorage
    localStorage.setItem('leadsTableLimit', limitValue.toString());
    // Update pagination state first, resetting page to 1
    setPagination(prev => ({ ...prev, limit: limitValue, page: 1 }));
    // Explicitly fetch leads with page 1 and the new limit value
    fetchLeads(1, limitValue);
  };

  // Handle lead creation
  const handleLeadCreated = (newLead) => {
    // Re-fetch leads for the current page to ensure correct sorting and pagination
    fetchLeads(pagination.page);
  };

  // Handle lead update
  const handleLeadUpdated = (updatedLead) => {
    // Update leads list
    const updatedLeads = leads.map(lead =>
      lead.id === updatedLead.id ? updatedLead : lead
    );
    setLeads(updatedLeads);
    
    // Update selectedLead if it's the one being updated
    if (selectedLead && selectedLead.id === updatedLead.id) {
      setSelectedLead(updatedLead);
    }
  };

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    // Update the filters state
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Apply filters
  const applyFilters = () => {
    // Only include filters with values
    const newActiveFilters = Object.entries(filters)
      .filter(([_, value]) => value !== '')
      .reduce((obj, [key, value]) => ({ ...obj, [key]: value }), {});

    // Save to localStorage
    localStorage.setItem('leadsActiveFilters', JSON.stringify(newActiveFilters));
    localStorage.setItem('leadsFilters', JSON.stringify(filters));
    
    setActiveFilters(newActiveFilters);
    setActiveSegment(null); // Clear active segment when applying new filters

    // Close filters panel on mobile
    if (window.innerWidth < 768) {
      localStorage.setItem('leadsShowFilters', 'false');
      setShowFilters(false);
    }
  };

  // Clear filters
  const clearFilters = () => {
    const emptyFilters = {
      assigned_to: '',
      status: '',
      country: '',
      original_source: '',
      latest_source: '',
      call_attempts_min: '',
      call_attempts_max: '',
      created_after: '',
      created_before: '',
      search: '',
      reconversion_date_after: '',
      reconversion_date_before: '',
      last_conversion_date_after: '',
      last_conversion_date_before: '',
      courses_bought: '',
      has_courses: '',
      no_courses: '',
      subscribed: ''
    };
    
    // Clear localStorage
    localStorage.removeItem('leadsActiveFilters');
    localStorage.setItem('leadsFilters', JSON.stringify(emptyFilters));
    
    setFilters(emptyFilters);
    setActiveFilters({});
    setActiveSegment(null); // Clear active segment when clearing filters
  };

  // Count active filters
  const activeFilterCount = Object.values(activeFilters).filter(Boolean).length;

  // Handle sorting
  const handleSort = (column) => {
    console.log('handleSort called with column:', column);

    if (sortBy === column) {
      // If already sorting by this column, toggle order
      const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
      console.log(`Toggling order for column ${column} to ${newOrder}`);
      setSortOrder(newOrder);
      // Save sort order to localStorage
      localStorage.setItem('leadsTableSortOrder', newOrder);
    } else {
      // Otherwise, set new sort column and default to ascending
      console.log(`Setting sort to column ${column} asc`);
      setSortBy(column);
      setSortOrder('asc');
      // Save sort column and order to localStorage
      localStorage.setItem('leadsTableSortBy', column);
      localStorage.setItem('leadsTableSortOrder', 'asc');
    }
  };

  // Handle select all leads checkbox
  const handleSelectAll = () => {
    if (selectAll) {
      // If currently "all selected", deselect all
      setSelectedLeads([]);
      setSelectAll(false);
    } else {
      // Select all leads on the current page
      const currentPageLeadIds = leads.map(lead => lead.id);
      setSelectedLeads(currentPageLeadIds);
      setSelectAll(true);
      
      console.log(`Selected ${currentPageLeadIds.length} leads from current page`);
    }
  };


  // Handle loading a segment
  const handleLoadSegment = async (segment) => {
    try {
      setLoading(true);
      setActiveSegment(segment); // Set active segment immediately
      // Fetch leads will be triggered by useEffect dependency on activeSegment
    } catch (error) {
      console.error('Error loading segment:', error);
      setError('Failed to load segment leads. Please try again.');
      setLoading(false); // Ensure loading is false on error
    }
  };

  // Function to open the bulk action modal for adding to a segment
  const handleOpenAddToSegment = async () => {
    try {
      // Fetch only manual segments
      const token = localStorage.getItem('token');
      const config = {
        headers: { 'x-auth-token': token },
        params: { type: 'manual' } // Filter for manual segments
      };
      const response = await axios.get('/api/segments', config);
      setManualSegments(response.data || []); // Store the manual segments

      // Set action and open the modal
      setBulkAction('addToSegment');
      setShowBulkActionModal(true);

    } catch (err) {
      console.error('Error fetching manual segments:', err);
      setError('Failed to load segments list. Please try again.');
    }
  };


  // Execute bulk action
  const executeBulkAction = async (action, actionData) => {
    try {
      const token = localStorage.getItem('token');

      const config = {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      };

      let updatedLeads = [...leads];

      if (action === 'delete') {
        try {
          // Use the bulk operations API for efficient deletion
          const response = await axios.post('/api/bulk-operations/delete', 
            { 
              table: 'leads', 
              ids: selectedLeads 
            }, 
            config
          );
          
          // Show notification with undo option
          if (response.data.operationId) {
            addUndoNotification({
              operationId: response.data.operationId,
              message: response.data.message || `Deleted ${response.data.deletedCount} leads`,
              expiresAt: response.data.expiresAt,
              onUndo: () => {
                // Refresh the leads list after restoration
                fetchLeads(pagination.page);
              }
            });
          }

          // For deletion actions, always fetch fresh data from the server
          // This ensures the table view is properly refreshed
          fetchLeads(pagination.page);
        } catch (err) {
          // Get the error message from the response
          const errorMessage = err.response?.data?.message || 'Failed to delete leads';

          // Show the error in a modal
          setErrorModalMessage(errorMessage);
          setShowErrorModal(true);
        }
      } else if (action === 'update') {
        // Update selected leads
        const updateData = {};

        // Only include fields with values
        if (actionData.status) updateData.status = actionData.status;
        if (actionData.country) updateData.country = actionData.country;
        
        // Special handling for subscribed (boolean field)
        if (actionData.subscribed !== undefined && actionData.subscribed !== '') {
          updateData.subscribed = actionData.subscribed === 'true';
        }

        // Special handling for call_attempts
        if (actionData.call_attempts) {
          if (actionData.call_attempts === 'increment') {
            // Increment call attempts for each lead
            for (const leadId of selectedLeads) {
              const leadToUpdate = leads.find(lead => lead.id === leadId);
              if (leadToUpdate && (leadToUpdate.call_attempts || 0) < 10) {
                await axios.patch(
                  `/api/leads/${leadId}/call-attempts`,
                  { increment: true },
                  config
                );
              }
            }
          } else if (actionData.call_attempts === 'reset') {
            updateData.call_attempts = 0;
          } else {
            // Set specific value
            updateData.call_attempts = parseInt(actionData.call_attempts, 10);
          }
        }

        // Special handling for assigned_to
        if (actionData.assigned_to) {
          // Handle special case for unassigning (value is "null" string)
          if (actionData.assigned_to === "null") {
            updateData.assigned_to = null;
          } else {
            updateData.assigned_to = actionData.assigned_to;
          }
        }

        // Only perform update if there's data to update
        if (Object.keys(updateData).length > 0) {
          // Update leads one by one - process ALL selectedLeads, not just those in current view
          const totalLeads = selectedLeads.length;
          let processedCount = 0;
          
          for (const leadId of selectedLeads) {
            try {
              processedCount++;
              // For leads in current view, we have the full data
              const leadToUpdate = leads.find(lead => lead.id === leadId);
              
              if (leadToUpdate) {
                // We have full lead data, merge with updates
                const updatedLead = { ...leadToUpdate, ...updateData };
                const res = await axios.put(`/api/leads/${leadId}`, updatedLead, config);
                
                // Update in local state if the lead is in current view
                updatedLeads = updatedLeads.map(lead => lead.id === leadId ? res.data : lead);
              } else {
                // Lead is selected but not in current view (selected via "Select all X leads")
                // Send a request with just the update data and lead ID
                await axios.put(`/api/leads/${leadId}`, updateData, config);
              }
              
              // Log progress for large updates
              if (totalLeads > 100 && processedCount % 50 === 0) {
                console.log(`Bulk update progress: ${processedCount}/${totalLeads} leads processed`);
              }
            } catch (err) {
              console.error(`Error updating lead ${leadId}:`, err);
              // Continue with other leads even if one fails
            }
          }
        }

        // Update state with changes for update actions
        setLeads(updatedLeads);
      } else if (action === 'addToSegment') {
          const segmentId = actionData.segmentId;
          if (!segmentId) {
              throw new Error("No segment selected");
          }
          await axios.post(`/api/segments/${segmentId}/leads`, { leadIds: selectedLeads }, config);
          // Optionally show success message or refresh segment counts
          console.log(`Added ${selectedLeads.length} leads to segment ${segmentId}`);
      }

      // Clear selection and close modals regardless of action type
      setSelectedLeads([]);
      setSelectAll(false);
      setShowBulkActionModal(false);
      setBulkAction('');

      // Update pagination total for any action
      if (action !== 'delete') { // Skip for delete since we're doing a full refresh
        // Recalculate total based on current leads length if needed, or rely on next fetch
        // For simplicity, we might just let the next fetch update the total
      }

    } catch (err) {
      console.error(`Error executing bulk action (${action}):`, err);

      // Only show the generic error if it's not already showing a specific error
      if (!showErrorModal) {
        // Get the error message from the response
        const errorMessage = err.response?.data?.message || `Failed to ${action} leads. Please try again.`;

        // Show the error in a modal
        setErrorModalMessage(errorMessage);
        setShowErrorModal(true);
      }
    }
  };


  // Helper function to generate page numbers for pagination
  const getPageNumbers = () => {
    const totalPages = pagination.pages;
    const currentPage = pagination.page;
    const pageNeighbours = 1; // How many pages to show on each side of the current page
    const totalNumbers = (pageNeighbours * 2) + 3; // Total page numbers to show (current + neighbours + first/last + ...)
    const totalBlocks = totalNumbers + 2; // Including the '...' placeholders

    if (totalPages <= totalBlocks) {
      // If total pages is small enough, show all page numbers
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const startPage = Math.max(2, currentPage - pageNeighbours);
    const endPage = Math.min(totalPages - 1, currentPage + pageNeighbours);
    let pages = [1]; // Always show the first page

    if (startPage > 2) {
      pages.push('...'); // Add '...' if there's a gap after the first page
    }

    // Add pages around the current page
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    if (endPage < totalPages - 1) {
      pages.push('...'); // Add '...' if there's a gap before the last page
    }

    pages.push(totalPages); // Always show the last page

    return pages;
  };

  return (
    <div className="w-full px-1 py-4 flex flex-col" id="leads-list-container">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Leads Management</h1>
          <p className="text-sm text-gray-500">
            {activeSegment ? (
              <span className="flex items-center">
                Viewing segment: <span className="font-medium text-blue-700 ml-1">{activeSegment.name}</span>
                <Button
                  variant="link"
                  onClick={() => {
                    // Create a hard reset function to ensure a complete state reset
                    const hardReset = async () => {
                      // First set loading to prevent flickering
                      setLoading(true);
                      
                      // Clear activeSegment - most important step
                      setActiveSegment(null);
                      
                      // Reset filter state to initial values
                      setFilters({
                        assigned_to: '',
                        status: '',
                        country: '',
                        original_source: '',
                        latest_source: '',
                        call_attempts_min: '',
                        call_attempts_max: '',
                        created_after: '',
                        created_before: '',
                        search: '',
                        reconversion_date_after: '',
                        reconversion_date_before: '',
                        subscribed: '',
                        courses_bought: '',
                        has_courses: '',
                        no_courses: ''
                      });
                      
                      // Explicitly reset activeFilters to an empty object
                      setActiveFilters({});
                      
                      // Short delay to ensure state updates have propagated
                      await new Promise(resolve => setTimeout(resolve, 50));
                      
                      // Force a clean API call with explicitly empty deal filters
                      try {
                        const token = localStorage.getItem('token');
                        if (!token) return;
                        
                        const cleanParams = {
                          page: 1,
                          limit: pagination.limit,
                          sort_by: sortBy,
                          sort_order: sortOrder,
                          // Explicitly set deal filters to empty
                          courses_bought: '',
                          has_courses: '',
                          no_courses: ''
                        };
                        
                        console.log('Hard reset with clean params:', cleanParams);
                        
                        const config = {
                          headers: {
                            'x-auth-token': token
                          },
                          params: cleanParams
                        };
                        
                        const response = await axios.get('/api/leads', config);
                        const leadsList = response.data.leads || [];
                        
                        // Update state
                        setLeads(leadsList);
                        setPagination({
                          page: parseInt(response.data.pagination?.page || 1),
                          limit: parseInt(response.data.pagination?.limit || 20),
                          total: parseInt(response.data.pagination?.total || 0),
                          pages: parseInt(response.data.pagination?.pages || 1)
                        });
                      } catch (err) {
                        console.error('Error in hard reset:', err);
                      } finally {
                        setLoading(false);
                      }
                    };
                    
                    // Execute the hard reset
                    hardReset();
                  }}
                  className="ml-2 text-xs"
                >
                  (Clear)
                </Button>
              </span>
            ) : (
              'Manage and track customer leads'
            )}
          </p>
        </div>
        <div className="flex flex-wrap mt-4 md:mt-0 gap-2">
        <div className="flex flex-wrap gap-1">
          {/* All Buttons with smaller padding and more compact layout */}
            {canCreate && (
              <Button
                onClick={() => setIsModalOpen(true)}
                variant="primary"
              >
                <svg className="-ml-0.5 mr-1.5 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                New Lead
              </Button>
            )}
            
            <Button
              onClick={() => setOpenLeadByIdModalOpen(true)}
              variant="outline"
              title="Find a lead by ID"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="-ml-0.5 mr-1.5 h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 21h7a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v11m0 5l4.879-4.879m0 0a3 3 0 104.243-4.242 3 3 0 00-4.243 4.242z" />
              </svg>
              Find
            </Button>
          
            <Button
              onClick={() => {
                const newValue = !showFilters;
                localStorage.setItem('leadsShowFilters', newValue.toString());
                setShowFilters(newValue);
              }}
              variant={activeFilterCount > 0 ? "primary" : "outline"}
              className={activeFilterCount > 0 ? 'bg-indigo-600 hover:bg-indigo-700 font-medium' : ''}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="-ml-0.5 mr-1.5 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              {activeFilterCount > 0 ? `Filters (${activeFilterCount})` : 'Filters'}
            </Button>
            
            {/* Segments button */}
            <Button
              onClick={() => setShowSegmentsListModal(true)}
              variant="outline"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="-ml-0.5 mr-1.5 h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              Segments
            </Button>
            
            {/* Save Segment button - only show when filters are active and user has permission */}
            {activeFilterCount > 0 && canCreateOwnSegments && (
              <Button
                onClick={() => setShowSaveSegmentModal(true)}
                variant="outline"
                className="border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100"
                title="Save current filters as a segment"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="-ml-0.5 mr-1.5 h-4 w-4 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                </svg>
                Save
              </Button>
            )}
            
            {/* Import/Export dropdown */}
            <div className="relative inline-block text-left">
              <div className="group">
                <Button
                  variant="outline"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="-ml-0.5 mr-1.5 h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 4H6a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-2m-4-1v8m0 0l3-3m-3 3l-3-3" />
                  </svg>
                  Import/Export
                  <svg className="-mr-1 ml-1 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </Button>
                <div className="hidden group-hover:block absolute z-50 mt-0 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5">
                  <div className="py-1" role="menu" aria-orientation="vertical">
                    {canImport && (
                      <Button
                        onClick={() => setIsImportModalOpen(true)}
                        variant="ghost"
                        className="w-full text-left block px-4 py-2 text-sm !text-gray-700 !bg-transparent hover:!bg-gray-100 !rounded-none !shadow-none !ring-0 !border-0 justify-start"
                        role="menuitem"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="mr-2 h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        Import CSV
                      </Button>
                    )}
                    
                    {canExport && (
                      <Button
                        onClick={() => {
                          const token = localStorage.getItem('token');
                          
                          axios({
                            url: '/api/leads/export/csv',
                            method: 'GET',
                            responseType: 'blob',
                            headers: {
                              'x-auth-token': token
                            },
                            params: activeFilters 
                          })
                          .then(response => {
                            const url = window.URL.createObjectURL(new Blob([response.data]));
                            const link = document.createElement('a');
                            link.href = url;
                            
                            const contentDisposition = response.headers['content-disposition'];
                            let filename = 'leads-export.csv';
                            if (contentDisposition) {
                              const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
                              if (filenameMatch && filenameMatch[1]) {
                                filename = filenameMatch[1];
                              }
                            }
                            
                            link.setAttribute('download', filename);
                            document.body.appendChild(link);
                            link.click();
                            
                            window.URL.revokeObjectURL(url);
                            document.body.removeChild(link);
                          })
                          .catch(error => {
                            console.error('Error exporting CSV:', error);
                            alert('Failed to export leads. Please try again.');
                          });
                        }}
                        variant="ghost"
                        className="w-full text-left block px-4 py-2 text-sm !text-gray-700 !bg-transparent hover:!bg-gray-100 !rounded-none !shadow-none !ring-0 !border-0 justify-start"
                        role="menuitem"
                      >
                        <div className="flex items-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="mr-2 h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                          Export CSV
                        </div>
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            <input 
              type="file" 
              accept=".csv" 
              ref={fileInputRef} 
              className="hidden" 
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  // Handle file upload
                  const file = e.target.files[0];
                  const formData = new FormData();
                  formData.append('csv', file);
                  
                  // Upload file
                  setCsvImportStatus({
                    ...csvImportStatus,
                    loading: true,
                    message: 'Uploading and processing CSV file...'
                  });
                  
                  const token = localStorage.getItem('token');
                  
                  axios.post('/api/leads/import/csv', formData, {
                    headers: {
                      'Content-Type': 'multipart/form-data',
                      'x-auth-token': token
                    }
                  }).then(response => {
                    setCsvImportStatus({
                      loading: false,
                      success: true,
                      message: response.data.message || `Successfully processed CSV file.`,
                      totalRows: response.data.totalRows,
                      importedCount: response.data.importedCount,
                      newLeadsCount: response.data.newLeadsCount,
                      updatedLeadsCount: response.data.updatedLeadsCount,
                      errors: response.data.errors || []
                    });
                    
                    // Refresh leads list
                    fetchLeads();
                    
                  }).catch(err => {
                    console.error('Error importing CSV:', err);
                    
                    setCsvImportStatus({
                      loading: false,
                      success: false,
                      message: err.response?.data?.message || 'Failed to import CSV file.',
                      totalRows: 0,
                      importedCount: 0,
                      errors: err.response?.data?.errors || []
                    });
                  });
                  
                  // Reset the input
                  e.target.value = '';
                }
              }}
            />
            
            
            <Button
              type="button"
              onClick={() => setShowColumnModal(true)}
              variant="outline"
              title="Manage visible columns"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="-ml-0.5 mr-1.5 h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
              </svg>
              Columns
            </Button>
          </div>
        </div>
      </div>
      
      {/* Filters Panel */}
      {showFilters && (
        <div className="mt-2 bg-white rounded-lg shadow-md p-3">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
            {/* Status Filter */}
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                Status
              </label>
              <select
                name="status"
                id="status"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={filters.status}
                onChange={handleFilterChange}
              >
                <option value="">All Statuses</option>
                <option value="new">New</option>
                <option value="connected">Connected</option>
                <option value="HOT">HOT</option>
                <option value="disqualified">Disqualified</option>
                <option value="converted">Converted</option>
                <option value="unreachable">Unreachable</option>
              </select>
            </div>
            
            {/* Assigned To Filter */}
            <div>
              <label htmlFor="assigned_to" className="block text-sm font-medium text-gray-700">
                Assigned To
              </label>
              <select
                name="assigned_to"
                id="assigned_to"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={filters.assigned_to}
                onChange={handleFilterChange}
              >
                <option value="">All Agents</option>
                <option value="unassigned">Unassigned</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Country Filter */}
            <div>
              <label htmlFor="country" className="block text-sm font-medium text-gray-700">
                Country
              </label>
              <input
                name="country"
                id="country"
                type="text"
                placeholder="Enter country name..."
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={filters.country}
                onChange={handleFilterChange}
              />
            </div>

            {/* Subscription Status Filter */}
            <div>
              <label htmlFor="subscribed" className="block text-sm font-medium text-gray-700">
                Subscription Status
              </label>
              <select
                name="subscribed"
                id="subscribed"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={filters.subscribed}
                onChange={handleFilterChange}
              >
                <option value="">All Leads</option>
                <option value="true">Subscribed</option>
                <option value="false">Unsubscribed</option>
              </select>
            </div>
            
            {/* Original Source Filter */}
            <div>
              <label htmlFor="original_source" className="block text-sm font-medium text-gray-700">
                Original Source
              </label>
              <input
                type="text"
                name="original_source"
                id="original_source"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={filters.original_source}
                onChange={handleFilterChange}
                placeholder="Filter by source (contains)..."
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 mt-2">
            {/* Search Filter */}
            <div>
              <label htmlFor="search" className="block text-sm font-medium text-gray-700">
                Search (Name, Email, Phone)
              </label>
              <input
                type="text"
                name="search"
                id="search"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={filters.search}
                onChange={handleFilterChange}
                placeholder="Search leads..."
              />
            </div>
            
            {/* Call Attempts Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Call Attempts
              </label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <input
                  type="number"
                  name="call_attempts_min"
                  min="0"
                  max="10"
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  value={filters.call_attempts_min}
                  onChange={handleFilterChange}
                  placeholder="Min"
                />
                <input
                  type="number"
                  name="call_attempts_max"
                  min="0"
                  max="10"
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  value={filters.call_attempts_max}
                  onChange={handleFilterChange}
                  placeholder="Max"
                />
              </div>
            </div>
            
            {/* Created Date Range */}
            <div>
              <label htmlFor="created_after" className="block text-sm font-medium text-gray-700">
                Created After
              </label>
              <input
                type="date"
                name="created_after"
                id="created_after"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={filters.created_after}
                onChange={handleFilterChange}
              />
            </div>
            
            <div>
              <label htmlFor="created_before" className="block text-sm font-medium text-gray-700">
                Created Before
              </label>
              <input
                type="date"
                name="created_before"
                id="created_before"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={filters.created_before}
                onChange={handleFilterChange}
              />
            </div>
          </div>
          
          {/* Reconversion Date Range */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 mt-2">
            <div>
              <label htmlFor="reconversion_date_after" className="block text-sm font-medium text-gray-700">
                Reconversion After
              </label>
              <input
                type="date"
                name="reconversion_date_after"
                id="reconversion_date_after"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={filters.reconversion_date_after}
                onChange={handleFilterChange}
              />
            </div>
            
            <div>
              <label htmlFor="reconversion_date_before" className="block text-sm font-medium text-gray-700">
                Reconversion Before
              </label>
              <input
                type="date"
                name="reconversion_date_before"
                id="reconversion_date_before"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={filters.reconversion_date_before}
                onChange={handleFilterChange}
              />
            </div>
            
            <div>
              <label htmlFor="last_conversion_date_after" className="block text-sm font-medium text-gray-700">
                Last Conversion After
              </label>
              <input
                type="date"
                name="last_conversion_date_after"
                id="last_conversion_date_after"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={filters.last_conversion_date_after}
                onChange={handleFilterChange}
              />
            </div>
            
            <div>
              <label htmlFor="last_conversion_date_before" className="block text-sm font-medium text-gray-700">
                Last Conversion Before
              </label>
              <input
                type="date"
                name="last_conversion_date_before"
                id="last_conversion_date_before"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={filters.last_conversion_date_before}
                onChange={handleFilterChange}
              />
            </div>
          </div>
          
          {/* Associated Deals Filter */}
          <div className="mt-2">
            <label htmlFor="courses_bought" className="block text-sm font-medium text-gray-700">
              Associated Deals
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-2 mt-1">
              <div className="flex items-center">
                <input
                  type="number"
                  min="0"
                  name="courses_bought"
                  id="courses_bought"
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  value={filters.courses_bought}
                  onChange={handleFilterChange}
                  placeholder="Number of deals"
                />
                <div className="ml-2 text-xs text-gray-500">
                  Enter a number to filter by exact count (leave empty for any)
                </div>
              </div>
              <div className="flex items-center">
                <input
                  id="has_courses"
                  name="has_courses"
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  checked={filters.has_courses === 'true'}
                  onChange={(e) => handleFilterChange({
                    target: {
                      name: 'has_courses',
                      value: e.target.checked ? 'true' : ''
                    }
                  })}
                />
                <label htmlFor="has_courses" className="ml-2 text-sm text-gray-700">
                  Has deals
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="no_courses"
                  name="no_courses"
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  checked={filters.no_courses === 'true'}
                  onChange={(e) => handleFilterChange({
                    target: {
                      name: 'no_courses',
                      value: e.target.checked ? 'true' : ''
                    }
                  })}
                />
                <label htmlFor="no_courses" className="ml-2 text-sm text-gray-700">
                  Has no deals
                </label>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end mt-2">
            <Button
              variant="primary"
              type="button"
              onClick={applyFilters}
            >
              Apply Filters
            </Button>
            
            <Button
              variant="outline"
              type="button"
              onClick={clearFilters}
              className="ml-3"
            >
              Clear Filters
            </Button>
          </div>
          
          <div className="flex flex-wrap gap-2 mt-2">
            {Object.entries(activeFilters).filter(([key, value]) => value).map(([key, value]) => {
              // Convert filter keys to display labels
              let displayKey = key.replace(/_/g, ' ');
              
              // Special case for courses_bought
              if (key === 'courses_bought') {
                displayKey = 'associated deals';
              }
              
              return (
                <span key={key} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {displayKey}: {value}
                </span>
              );
            })}
            
            <Button
              variant="link"
              onClick={clearFilters}
              className="text-xs"
            >
              Clear All
            </Button>
          </div>
        </div>
      )}
      
      
      {/* CSV Import Status */}
      {csvImportStatus.message && (
        <div className={`p-2 mb-2 rounded-md ${
          csvImportStatus.success 
            ? 'bg-green-50 border border-green-200 text-green-700' 
            : 'bg-red-50 border border-red-200 text-red-600'
        }`}>
          <div className="flex">
            <div className="flex-shrink-0">
              {csvImportStatus.loading ? (
                <svg className="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : csvImportStatus.success ? (
                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <div className="ml-3 flex-1">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm">{csvImportStatus.message}</p>
                  {csvImportStatus.errors && csvImportStatus.errors.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs text-red-600 font-medium">
                        {csvImportStatus.errors.length} {csvImportStatus.errors.length === 1 ? 'error' : 'errors'} occurred:
                      </p>
                      <ul className="list-disc list-inside ml-5 text-xs text-gray-600 mt-1">
                        {csvImportStatus.errors.slice(0, 3).map((error, index) => (
                          <li key={index}>{error}</li>
                        ))}
                        {csvImportStatus.errors.length > 3 && (
                          <li>...and {csvImportStatus.errors.length - 3} more errors</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
                
                {/* Close button */}
                <button 
                  onClick={() => setCsvImportStatus({...csvImportStatus, message: ''})}
                  className="ml-auto flex-shrink-0 text-gray-400 hover:text-gray-500 focus:outline-none"
                >
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Error Alert */}
      {error && (
        <div className="p-2 mb-2 rounded-md bg-red-50 border border-red-200 text-red-600">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm">{error}</p>
              <Button
                variant="link"
                onClick={() => fetchLeads()}
                className="mt-2 text-sm text-red-600 hover:text-red-500"
              >
                Retry
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* New Lead Modal */}
      <NewLeadModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        onLeadCreated={handleLeadCreated} 
      />
      
      {/* Open Lead By ID Modal */}
      <OpenLeadByIdModal
        isOpen={openLeadByIdModalOpen}
        onClose={() => setOpenLeadByIdModalOpen(false)}
        onLeadFound={(leadId) => {
          openLeadInNewTab(leadId);
        }}
      />
      
      {/* View Lead Modal */}
      {selectedLead && (
        <LeadDetailsModal
          isOpen={viewModalOpen}
          onClose={() => setViewModalOpen(false)}
          lead={selectedLead}
          users={users}
          onLeadUpdate={(updatedLead) => {
            handleLeadUpdated(updatedLead);
          }}
          onOpenEditModal={() => {
            setEditModalOpen(true);
          }}
          onOpenEmailModal={() => {
            setEmailModalOpen(true);
          }}
        />
      )}
      
      {/* Edit Lead Modal */}
      {selectedLead && (
        <EditLeadModal
          isOpen={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          lead={selectedLead}
          onLeadUpdated={handleLeadUpdated}
        />
      )}
      
      {/* Email Modal */}
      {selectedLead && (
        <EmailModal
          isOpen={emailModalOpen}
          onClose={() => setEmailModalOpen(false)}
          lead={selectedLead}
        />
      )}
      
      {/* CSV Guide Modal - Legacy */}
      <CsvGuideModal
        isOpen={showCsvGuide}
        onClose={() => setShowCsvGuide(false)}
        fileInputRef={fileInputRef}
      />
      
      {/* New CSV Import Modal with Column Mapping */}
      <LeadsImportModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onImportComplete={(results) => {
          if (results.success) {
            setCsvImportStatus({
              loading: false,
              success: true,
              message: `Successfully imported ${results.importedCount} leads (${results.newCount} new, ${results.updatedCount} updated)`,
              totalRows: results.totalRows || 0,
              importedCount: results.importedCount || 0,
              newLeadsCount: results.newCount || 0,
              updatedLeadsCount: results.updatedCount || 0,
              errors: results.errors || []
            });
            
            // Refresh the lead list
            fetchLeads();
          }
        }}
      />
      
      
      {/* Bulk Action Modal */}
      <BulkActionModal
        isOpen={showBulkActionModal}
        onClose={() => {
          setShowBulkActionModal(false);
          setBulkAction('');
        }}
        selectedLeads={selectedLeads}
        users={users}
        manualSegments={manualSegments} // Pass manual segments to the modal
        bulkAction={bulkAction} // Pass the current bulk action type
        onExecuteBulkAction={executeBulkAction}
      />
      
      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal 
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => {
          executeBulkAction('delete', {});
          setShowDeleteConfirm(false);
        }}
        message={`Are you sure you want to delete ${selectedLeads.length} lead${selectedLeads.length !== 1 ? 's' : ''}? This action cannot be undone.`}
      />
      
      {/* Error Modal for deletion errors */}
      <ConfirmationModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        onConfirm={() => setShowErrorModal(false)}
        title="Cannot Delete Lead"
        message={errorModalMessage}
        confirmButtonText="OK"
        confirmButtonClass="bg-blue-600 hover:bg-blue-700 focus:ring-blue-500"
        iconComponent={
          <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
            <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
        }
      />

      {/* Save Segment Modal */}
      <SaveSegmentModal
        isOpen={showSaveSegmentModal}
        onClose={() => setShowSaveSegmentModal(false)}
        filters={filters}
        activeFilters={activeFilters}
        users={users}
      />

      {/* Segments List Modal */}
      <SegmentsListModal
        isOpen={showSegmentsListModal}
        onClose={() => setShowSegmentsListModal(false)}
        onSelectSegment={handleLoadSegment}
      />
      
      {leads.length === 0 && !loading ? (
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <p>No leads found. Create your first lead to get started.</p>
        </div>
      ) : (
        <div className="w-full">
          <div className="bg-white shadow-md rounded-lg mb-0 relative flex flex-col w-full">
            
            {/* Bulk Actions Bar - only shown when leads are selected */}
            {selectedLeads.length > 0 && (
              <div className="bg-blue-50 border-b border-blue-200 p-2 flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <span className="text-sm font-medium text-blue-700">
                    {selectedLeads.length} lead{selectedLeads.length !== 1 ? 's' : ''} selected
                  </span>
                  <Button
                    variant="link"
                    onClick={() => {
                      setSelectedLeads([]);
                      setSelectAll(false);
                    }}
                    className="text-xs"
                  >
                    Clear selection
                  </Button>
                  <Button
                    variant="link"
                    onClick={async () => {
                      try {
                        setLoading(true);
                        const token = localStorage.getItem('token');
                        
                        // Get all lead IDs with the current filters
                        const params = {
                          ...activeFilters,
                          page: 1,
                          limit: 9999, // Request maximum possible leads
                          return_ids_only: true // Special parameter to request only IDs
                        };
                        
                        // If active segment is set, include the segment ID
                        if (activeSegment) {
                          params.segmentId = activeSegment.id;
                        }
                        
                        const config = {
                          headers: { 'x-auth-token': token },
                          params: params
                        };
                        
                        console.log('Fetching all lead IDs with params:', params);
                        const response = await axios.get('/api/leads', config);
                        
                        // Check if we received the IDs array
                        if (response.data && response.data.lead_ids && Array.isArray(response.data.lead_ids)) {
                          console.log(`Received ${response.data.lead_ids.length} lead IDs from API`);
                          // Set all lead IDs to selected
                          setSelectedLeads(response.data.lead_ids);
                          // Keep selectAll true for current page visual indicator
                          setSelectAll(true);
                        } else {
                          console.error('API response did not contain lead_ids array:', response.data);
                          setError('Failed to select all leads: API response format error');
                        }
                      } catch (err) {
                        console.error('Error selecting all leads:', err);
                        setError('Failed to select all leads. Please try again.');
                      } finally {
                        setLoading(false);
                      }
                    }}
                    className="text-xs text-blue-700"
                  >
                    {loading ? 'Loading...' : `Select all ${pagination.total} leads`}
                  </Button>
                </div>
                <div className="flex space-x-2">
                  {hasPermission('leads', 'update') && (
                    <Button
                      variant="primary"
                      onClick={() => {
                        setBulkAction('update');
                        setBulkActionData({ status: '', assigned_to: '', country: '', call_attempts: '' });
                        setShowBulkActionModal(true);
                      }}
                      size="sm"
                    >
                      Update/Assign
                    </Button>
                  )}
                  {hasPermission('leads', 'delete') && (
                    <Button
                      variant="danger"
                      onClick={() => setShowDeleteConfirm(true)}
                      size="sm"
                    >
                      Delete
                    </Button>
                  )}
                  {/* Add to Segment Button - Conditionally render based on permission? */}
                  {/* Assuming update permission allows adding to segments */}
                  {hasPermission('leads', 'update') && (
                    <Button
                      variant="outline"
                      onClick={handleOpenAddToSegment} // Updated onClick handler
                      size="sm"
                    >
                      Add to Segment
                    </Button>
                  )}
                </div>
              </div>
            )}
            
            {/* Table with SharedTable component */}
            <LeadsTableWithShared
              leads={leads}
              selectedLeads={selectedLeads}
              setSelectedLeads={setSelectedLeads}
              selectAll={selectAll}
              setSelectAll={setSelectAll}
              handleSelectAll={handleSelectAll}
              dealCounts={dealCounts}
              setSelectedLead={setSelectedLead}
              setViewModalOpen={setViewModalOpen}
              setEmailModalOpen={setEmailModalOpen}
              sortBy={sortBy}
              sortOrder={sortOrder}
              users={users}
              setLeads={setLeads}
              handleSort={handleSort}
              showColumnModal={showColumnModal}
              setShowColumnModal={setShowColumnModal}
              pagination={pagination}
              onPageChange={(page) => {
                console.log('LeadsList - Page change requested to', page);
                handlePageChange(page);
              }}
              onLimitChange={handleLimitChange} // Pass the handler
              // fetchLeads={fetchLeads} // Removed fetchLeads prop
              loading={loading}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadsListWithShared;
